import { useCallback, useEffect, useRef, useState } from "react";

interface BrowserSpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: { results: ArrayLike<{ [index: number]: { transcript: string } }> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface BrowserSpeechRecognitionConstructor {
  new (): BrowserSpeechRecognition;
}

type SpeechRecognitionWindow = Window & {
  SpeechRecognition?: BrowserSpeechRecognitionConstructor;
  webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
};

const getConstructor = () => {
  if (typeof window === "undefined") return undefined;
  const speechWindow = window as SpeechRecognitionWindow;
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
};

export default function useSpeechRecognition({
  value,
  onChange,
  onError,
  language,
}: {
  value: string;
  onChange: (value: string) => void;
  onError?: (message: string) => void;
  language: string;
}) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported] = useState(() => Boolean(getConstructor()));
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const inputBaseRef = useRef("");
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);

  valueRef.current = value;
  onChangeRef.current = onChange;
  onErrorRef.current = onError;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const toggle = useCallback(() => {
    if (isListening) {
      stop();
      return;
    }

    const SpeechRecognition = getConstructor();
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;
    inputBaseRef.current = valueRef.current.trimEnd();
    recognition.onresult = (event) => {
      let transcript = "";
      for (let index = 0; index < event.results.length; index += 1) {
        transcript += event.results[index][0]?.transcript ?? "";
      }
      const base = inputBaseRef.current;
      onChangeRef.current(`${base}${base && transcript ? " " : ""}${transcript}`);
    };
    recognition.onerror = () => {
      setIsListening(false);
      onErrorRef.current?.("Voice transcription failed. Try again.");
    };
    recognition.onend = () => {
      setIsListening(false);
      if (recognitionRef.current === recognition) recognitionRef.current = null;
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsListening(true);
    } catch {
      recognitionRef.current = null;
      onErrorRef.current?.("Voice transcription could not be started.");
    }
  }, [isListening, language, stop]);

  useEffect(
    () => () => {
      const recognition = recognitionRef.current;
      if (recognition) recognition.abort();
    },
    []
  );

  return { isSupported, isListening, toggle, stop };
}
